import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Shield, ChevronDown, ChevronUp, Cookie } from 'lucide-react';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface ConsentRecord {
  preferences: CookiePreferences;
  timestamp: string;
  version: string;
}

const CONSENT_KEY = 'listbright_cookie_consent';
const CONSENT_VERSION = '1.0';

function getStoredConsent(): ConsentRecord | null {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as ConsentRecord;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function storeConsent(preferences: CookiePreferences) {
  const record: ConsentRecord = {
    preferences,
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(record));
}

const defaultPreferences: CookiePreferences = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
};

export function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const existing = getStoredConsent();
    if (!existing) {
      // Small delay so it doesn't flash on load
      const timer = setTimeout(() => setVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = useCallback(() => {
    const all: CookiePreferences = { necessary: true, functional: true, analytics: true, marketing: true };
    storeConsent(all);
    setVisible(false);
  }, []);

  const handleRejectAll = useCallback(() => {
    const minimal: CookiePreferences = { necessary: true, functional: false, analytics: false, marketing: false };
    storeConsent(minimal);
    setVisible(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    storeConsent(preferences);
    setVisible(false);
  }, [preferences]);

  if (!visible) return null;

  const categories = [
    {
      key: 'necessary' as const,
      label: 'Strictly Necessary',
      description: 'Required for the site to function. Cannot be disabled.',
      locked: true,
    },
    {
      key: 'functional' as const,
      label: 'Functional',
      description: 'Remember your preferences like language, currency, and region settings.',
      locked: false,
    },
    {
      key: 'analytics' as const,
      label: 'Analytics',
      description: 'Help us understand how visitors use the site so we can improve it.',
      locked: false,
    },
    {
      key: 'marketing' as const,
      label: 'Marketing',
      description: 'Used to deliver relevant ads and track campaign effectiveness.',
      locked: false,
    },
  ];

  // Slim mode = collapsed bottom bar; doesn't block hero CTAs.
  // Expands to full panel only when the user clicks "Customize preferences".
  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-2 sm:p-3 animate-in slide-in-from-bottom-full duration-500 pointer-events-none">
      <div className="mx-auto max-w-4xl rounded-xl border border-border bg-background/95 backdrop-blur shadow-elevated pointer-events-auto">
        {!showDetails ? (
          // Slim bar: single row on desktop, compact on mobile
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Cookie className="h-4 w-4 text-primary shrink-0" />
              <p className="text-xs sm:text-sm text-muted-foreground truncate">
                We use cookies to improve your experience.{' '}
                <Link to="/cookies" className="text-primary underline underline-offset-2">
                  Read our cookie policy
                </Link>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setShowDetails(true)}
                className="text-xs font-medium text-muted-foreground hover:text-foreground px-2 py-1.5"
              >
                Customize
              </button>
              <Button variant="outline" size="sm" onClick={handleRejectAll} className="h-8 text-xs">
                Reject
              </Button>
              <Button size="sm" onClick={handleAcceptAll} className="h-8 text-xs">
                Accept All
              </Button>
            </div>
          </div>
        ) : (
        <>
        {/* Header */}
        <div className="flex items-start gap-3 p-5 pb-3">
          <div className="mt-0.5 rounded-lg bg-primary/10 p-2">
            <Cookie className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-semibold text-foreground">We value your privacy</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              We use cookies to enhance your experience, analyze traffic, and personalize content.
              Read our{' '}
              <Link to="/cookies" className="text-primary underline underline-offset-2 hover:text-primary/80">
                Cookie Policy
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-primary underline underline-offset-2 hover:text-primary/80">
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>

        {/* Expandable preferences */}
        <div className="px-5">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="flex w-full items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1"
          >
            <Shield className="h-3.5 w-3.5" />
            Customize preferences
            {showDetails ? <ChevronUp className="h-3.5 w-3.5 ml-auto" /> : <ChevronDown className="h-3.5 w-3.5 ml-auto" />}
          </button>

          {showDetails && (
            <div className="mt-2 space-y-3 pb-1">
              {categories.map((cat) => (
                <div key={cat.key} className="flex items-center justify-between gap-4 rounded-lg bg-muted/50 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{cat.label}</p>
                    <p className="text-xs text-muted-foreground">{cat.description}</p>
                  </div>
                  <Switch
                    checked={preferences[cat.key]}
                    onCheckedChange={(checked) =>
                      !cat.locked && setPreferences((prev) => ({ ...prev, [cat.key]: checked }))
                    }
                    disabled={cat.locked}
                    className="shrink-0"
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-5 pt-3">
          {showDetails ? (
            <>
              <Button variant="outline" onClick={handleRejectAll} className="flex-1 sm:flex-none h-12 sm:h-9 text-base sm:text-sm">
                Reject All
              </Button>
              <Button variant="outline" onClick={handleSavePreferences} className="flex-1 sm:flex-none h-12 sm:h-9 text-base sm:text-sm">
                Save Preferences
              </Button>
              <Button onClick={handleAcceptAll} className="flex-1 sm:flex-none h-12 sm:h-9 text-base sm:text-sm">
                Accept All
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleRejectAll} className="flex-1 sm:flex-none h-12 sm:h-9 text-base sm:text-sm">
                Reject All
              </Button>
              <Button onClick={handleAcceptAll} className="flex-1 sm:flex-none h-12 sm:h-9 text-base sm:text-sm">
                Accept All
              </Button>
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
  );
}
